import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, ChevronDown, ChevronRight, X } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/store/useAppStore'

const STEPS = [
  { id: 'settings_complete', title: 'Complétez votre profil',        description: 'Ajoutez votre statut juridique et objectif CA', icon: '⚙️', link: '/app/settings'     },
  { id: 'first_candidate',   title: 'Ajoutez votre premier candidat', description: 'Créez une fiche dans votre vivier',              icon: '👤', link: '/app/vivier'       },
  { id: 'first_mandate',     title: 'Créez votre premier mandat',     description: 'Enregistrez un besoin client',                   icon: '📋', link: '/app/mandats'      },
  { id: 'first_commission',  title: 'Enregistrez une commission',     description: 'Saisissez votre première commission du mois',    icon: '💰', link: '/app/commissions'  },
]

export default function OnboardingChecklist() {
  const { user } = useAppStore()
  const [steps,     setSteps]     = useState<string[]>([])
  const [isOpen,    setIsOpen]    = useState(true)
  const [dismissed, setDismissed] = useState(false)
  const [loaded,    setLoaded]    = useState(false)

  // Charger état initial
  useEffect(() => {
    if (!user) return
    supabase
      .from('users')
      .select('onboarding_completed, onboarding_steps_done')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.onboarding_completed) { setDismissed(true); return }
        setSteps(data?.onboarding_steps_done ?? [])
        setLoaded(true)
      })
  }, [user?.id])

  // Détecter étapes complétées
  useEffect(() => {
    if (!user || !loaded) return
    const detect = async () => {
      const [
        { count: candCount },
        { count: mandCount },
        { count: commCount },
        profileRes,
      ] = await Promise.all([
        supabase.from('candidates').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('mandates').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('commissions').select('id', { count: 'exact', head: true }).eq('user_id', user.id),
        supabase.from('users').select('legal_status, ca_objective_monthly').eq('id', user.id).single(),
      ])
      const done: string[] = []
      if (profileRes.data?.legal_status && profileRes.data?.ca_objective_monthly) done.push('settings_complete')
      if ((candCount ?? 0) > 0) done.push('first_candidate')
      if ((mandCount ?? 0) > 0) done.push('first_mandate')
      if ((commCount ?? 0) > 0) done.push('first_commission')
      setSteps(done)
      const allDone = done.length === STEPS.length
      await supabase.from('users').update({
        onboarding_steps_done: done,
        ...(allDone ? { onboarding_completed: true } : {}),
      }).eq('id', user.id)
      if (allDone) setTimeout(() => setDismissed(true), 3000)
    }
    detect()
  }, [user?.id, loaded])

  if (dismissed || !loaded) return null

  const progress = Math.round((steps.length / STEPS.length) * 100)

  if (!isOpen) {
    return (
      <div className="fixed bottom-20 md:bottom-6 right-4 z-40">
        <button
          onClick={() => setIsOpen(true)}
          className="bg-[#4F46E5] text-white rounded-full px-4 py-2.5 shadow-lg flex items-center gap-2 text-sm font-semibold hover:bg-[#4338CA] transition"
        >
          🚀 Démarrage ({steps.length}/{STEPS.length})
        </button>
      </div>
    )
  }

  return (
    <div className="fixed bottom-20 md:bottom-6 right-4 z-40 w-80">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#4F46E5] to-[#4338CA] p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-white font-semibold text-sm">Démarrage rapide</p>
              <p className="text-indigo-200 text-xs mt-0.5">{steps.length}/{STEPS.length} étapes complétées</p>
            </div>
            <button onClick={() => setIsOpen(false)} className="text-indigo-200 hover:text-white transition p-1">
              <ChevronDown size={16} />
            </button>
          </div>
          <div className="h-1.5 bg-indigo-500/50 rounded-full overflow-hidden">
            <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Steps */}
        <div className="p-3 space-y-1">
          {STEPS.map(step => {
            const done = steps.includes(step.id)
            return (
              <Link
                key={step.id}
                to={step.link}
                onClick={e => { if (done) e.preventDefault() }}
                className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors ${
                  done ? 'opacity-50 cursor-default' : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-sm ${
                  done ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-slate-100 dark:bg-slate-700'
                }`}>
                  {done ? <CheckCircle2 size={16} className="text-emerald-600" /> : step.icon}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${done ? 'line-through text-slate-400' : 'text-slate-800 dark:text-slate-200'}`}>
                    {step.title}
                  </p>
                  {!done && <p className="text-xs text-slate-400 dark:text-slate-500">{step.description}</p>}
                </div>
                {!done && <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />}
              </Link>
            )
          })}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 pt-1 border-t border-slate-100 dark:border-slate-700">
          <button
            onClick={async () => {
              await supabase.from('users').update({ onboarding_completed: true }).eq('id', user?.id)
              setDismissed(true)
            }}
            className="text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 w-full text-center flex items-center justify-center gap-1 py-1"
          >
            <X size={11} /> Ne plus afficher
          </button>
        </div>
      </div>
    </div>
  )
}
